<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import {
    FilterModal,
    SearchCombobox,
    SelectInput,
    ThemeProvider,
  } from '@/shared/ui';
  import { usePermittedMembers } from '@/entities/User';
  import {
    formatDateForApi,
    parseCivilDate,
    useCustomToast,
  } from '@/shared/lib';
  import { listMatrixCategories } from '../api/matrix-categories.api';
  import FilterDateField from './FilterDateField.vue';
  import type { ModelsListFilters } from '../lib/models-list-query';

  const ALL_CATEGORIES_OPTION = { id: '', name: 'Todas as categorias' };

  const props = defineProps<{
    currentFilters?: ModelsListFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: ModelsListFilters];
    'clear-filters': [];
  }>();

  const { showErrorToast } = useCustomToast();

  const draftDate = ref<Date | null>(null);
  const draftCategoryId = ref<string | number>('');
  const categoryOptions = ref<{ id: string | number; name: string }[]>([
    ALL_CATEGORIES_OPTION,
  ]);

  const {
    userOptions: memberOptions,
    isLoading: isMembersLoading,
    fetchUsers,
  } = usePermittedMembers({ includeInactiveMembers: true });
  const memberSearchQuery = ref('');
  const draftCreatedBy = ref('');

  const memberComboboxOptions = computed(() => {
    const query = memberSearchQuery.value.trim().toLowerCase();
    return memberOptions.value
      .filter((member) => !query || member.name.toLowerCase().includes(query))
      .map((member) => ({ value: String(member.id), label: member.name }));
  });

  const loadFromFilters = (filters?: ModelsListFilters) => {
    draftDate.value = parseCivilDate(filters?.createdAt);
    draftCategoryId.value = filters?.categoryId ?? '';
    draftCreatedBy.value = filters?.createdBy ? String(filters.createdBy) : '';
  };

  loadFromFilters(props.currentFilters);

  watch(() => props.currentFilters, loadFromFilters, { deep: true });

  onMounted(async () => {
    void fetchUsers();

    try {
      const categories = await listMatrixCategories();
      categoryOptions.value = [ALL_CATEGORIES_OPTION, ...categories];
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      showErrorToast({
        title: 'Erro ao carregar categorias',
        message: 'Tente novamente mais tarde.',
        type: 'error',
      });
    }
  });

  const handleApply = () => {
    emit('update:filter', {
      createdAt: draftDate.value ? (formatDateForApi(draftDate.value) ?? undefined) : undefined,
      categoryId: draftCategoryId.value ? Number(draftCategoryId.value) : undefined,
      createdBy: draftCreatedBy.value ? Number(draftCreatedBy.value) : undefined,
    });
  };

  const handleClear = () => {
    draftDate.value = null;
    draftCategoryId.value = '';
    draftCreatedBy.value = '';
    memberSearchQuery.value = '';
    emit('clear-filters');
  };

  // Closing has to undo the draft here: the modal instance outlives one opening, so a
  // discarded selection would come back the next time it opens.
  const handleClose = () => {
    loadFromFilters(props.currentFilters);
    emit('close');
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-sm"
    @close="handleClose"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="flex flex-col gap-4 px-6 py-5">
          <FilterDateField
            v-model="draftDate"
            trigger-test-id="models-filter-date-trigger"
          />

          <div class="flex flex-col gap-1.5">
            <h4 class="text-sm font-medium text-gray-07">
              Categoria
            </h4>
            <SelectInput
              :model-value="draftCategoryId"
              :options="categoryOptions"
              label="Selecione a categoria"
              container-class="w-full min-w-64"
              data-testid="models-filter-category-select"
              @update:model-value="draftCategoryId = $event"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <h4 class="text-sm font-medium text-gray-07">
              Elaborador
            </h4>
            <SearchCombobox
              :model-value="draftCreatedBy"
              :options="memberComboboxOptions"
              :loading="isMembersLoading"
              placeholder="Selecione um colaborador"
              search-placeholder="Buscar colaborador..."
              trigger-icon="arrow-down"
              class="w-full h-11"
              data-testid="models-filter-creator-combobox"
              @update:model-value="draftCreatedBy = $event || ''"
              @search="memberSearchQuery = $event"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
